import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/lib/i18n';
import { api, errMsg } from '@/lib/api';
import { AuthLayout } from '@/pages/LoginPage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';

export default function RegisterPage({ inviteMode = false }) {
  const { register, user, login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('register');

  useEffect(() => {
    if (inviteMode && token) {
      api.get(`/invitations/${token}`).then(({ data }) => {
        setInvite(data);
        setForm((f) => ({ ...f, email: data.email }));
      }).catch((e) => toast.error(errMsg(e)));
    }
  }, [inviteMode, token]);

  // Already logged in and visiting an invite link -> accept directly
  useEffect(() => {
    if (inviteMode && token && user) {
      api.post(`/invitations/${token}/accept`).then(({ data }) => {
        toast.success('Undangan diterima');
        localStorage.setItem('pillot_company_id', data.company_id);
        window.location.href = '/apps';
      }).catch((e) => toast.error(errMsg(e)));
    }
  }, [inviteMode, token, user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(form.email.trim(), form.password);
        if (token) {
          const { data } = await api.post(`/invitations/${token}/accept`);
          localStorage.setItem('pillot_company_id', data.company_id);
        }
        window.location.href = '/apps';
        return;
      }
      if (!agree) { toast.error('Centang persetujuan Syarat & Ketentuan dan Kebijakan Privasi terlebih dahulu'); return; }
      const data = await register({ ...form, email: form.email.trim(), invite_token: token || undefined, accept_terms: agree });
      navigate(data.companies?.length ? '/apps' : '/onboarding');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const title = inviteMode && invite ? `Bergabung ke ${invite.company_name}` : t('register_title');
  const subtitle = inviteMode && invite ? `Anda diundang sebagai ${t(invite.role)}. Buat akun atau masuk untuk menerima undangan.` : t('register_sub');

  return (
    <AuthLayout title={title} subtitle={subtitle} footer={!inviteMode && <>{t('have_account')} <Link to="/login" className="font-medium text-primary hover:underline" data-testid="register-login-link">{t('login')}</Link></>}>
      {inviteMode && (
        <div className="mb-4 inline-flex rounded-xl border border-border p-0.5 text-sm">
          {['register', 'login'].map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} className={`rounded-lg px-3 py-1.5 ${mode === m ? 'bg-primary text-white' : 'text-muted-foreground'}`} data-testid={`invite-mode-${m}`}>{t(m)}</button>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {mode === 'register' && (
          <div className="space-y-1.5">
            <Label htmlFor="full_name">{t('full_name')}</Label>
            <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="register-name-input" className="rounded-xl h-11" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email">{t('email')}</Label>
          <Input id="email" type="email" required value={form.email} readOnly={inviteMode && !!invite} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" className="rounded-xl h-11" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">{t('password')}</Label>
          <PasswordInput id="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="register-password-input" className="rounded-xl h-11" />
          <p className="text-xs text-muted-foreground">Minimal 6 karakter</p>
        </div>
        {mode === 'register' && (
          <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm cursor-pointer" data-testid="register-terms-label">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="register-terms-checkbox" />
            <span className="text-muted-foreground leading-snug">Saya menyetujui <Link to="/terms" target="_blank" className="font-medium text-primary hover:underline" data-testid="register-terms-link">Syarat & Ketentuan</Link> dan <Link to="/privacy" target="_blank" className="font-medium text-primary hover:underline" data-testid="register-privacy-link">Kebijakan Privasi</Link> Pillot.</span>
          </label>
        )}
        <Button type="submit" disabled={busy || (mode === 'register' && !agree)} className="w-full h-11 rounded-xl" data-testid="register-form-submit-button">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <>{mode === 'login' ? t('login') : t('register')} <ArrowRight size={16} className="ml-1" /></>}
        </Button>
      </form>
    </AuthLayout>
  );
}
